const engine = require('store/src/store-engine')
const storages = [
	require('store/storages/sessionStorage'),
	require('store/storages/localStorage'),
	require('store/storages/cookieStorage')
]
const plugins = [
	require('store/plugins/expire')
]

const utils = require('../lib/utils')
const appConfig = require('../lib/config').getConfig()
const store = require('../lib/store')
const User = require('./user')
const ComponentTree = require('../lib/componentTree')
const translationManager = require('./translationManager')

const clientStore = engine.createStore(storages, plugins)
const defaultStorageContent = {
	container: {},
	app: {},
	session: {
		user: {}
	},
	sessionID: '',
	isClient: true,
	isServer: false
}
let domain = ''

store.isClient = true

/**
 * init client store
 */
store.init = function(){
	// init
	let storage;
	if(window.location) domain = '-' + utils.getKeyFromValue(window.location.hostname);

	// load data
	storage = clientStore.get(appConfig.store.name + domain) || defaultStorageContent
	// initialize browser side componentTree
	storage.componentTree = new ComponentTree()
	// the browser does not need to handle more than one storage.
	// so the storage list never ever contain more than its client storage.
	this.storages = {}
	this.storages[appConfig.requestIDFallback + '_' + appConfig.sessionIDFallback + '_' + appConfig.serviceDomainFallback] = storage
};

/**
 * update client object
 */
store.beforeSet = function(storage, newData){
	if((!storage.user || !(storage.user instanceof User))){
		storage.user = new User(storage);
	}
	if(newData && newData.session && newData.session.user) {
		storage.user.update(newData.session.user);
	}

	// set some stuff which should not be merged but replaced instead.
	if(newData.container) 	storage.container = newData.container;
	if(newData.app) 		storage.app = newData.app;
	if(newData.request) 	storage.request = newData.request;
};

/**
 * save storage to browser local storage
 *
 * @param   storage         		object - actual storage
 * @param   updateServer			boolean - default: false - should the server be updated according to the given data?
 * @param   updateServerCallback	object - optional - { function: <callbackFunction>, context: <callbackContext> }
 */
store.afterSet = function(storage, updateServer, updateServerCallback){
	// set data within browsers' storage
	// NOTE: prevent componentTree and user instance from being saved to local browsers' storage
	clientStore.set(appConfig.store.name + domain, utils.replace(storage, { componentTree: undefined, buildMemory: undefined, user: undefined }), new Date().getTime() + appConfig.store.maxAge);

	// check if session.data has changed at all before bothering the server with a session update of this user!
	const sessionDataHash = utils.collectionMD5({
		sessionData: utils.replace((storage.session || {}).data || {}, { serviceCollectionSecret: undefined }),
		upStartTimestamp: (storage.app || {}).upStartTimestamp || 0,
	});
	if(updateServer && storage.session && storage.session.data && storage.session.dataHash !== sessionDataHash){
		storage.session.dataHash = sessionDataHash
		return store.updateSessionData(storage.session.data, storage, updateServerCallback);
	}

	return utils.promise.resolve();
};

/**
 * this function is used to send the new sessionData to the server.
 *
 * @param 	sessionData		object - session data stored in storage.session.data (server- and client-side)
 * @param   storage         object - actual storage
 * @param   callback		object - optional - { function: <callbackFunction>, context: <callbackContext> }
 * @returns Promise
 */
store.updateSessionData = function(sessionData, storage, callback){
	return $.ajax({
		url: '/~/clientInterface/updateSessionData',
		method: 'POST',
		data: JSON.stringify(sessionData)
	}).then(function(response){
		if(response.serviceCollectionSecret && response.assignedServiceUrls && response.assignedServiceUrls.length > 0){
			// tell all assigned services to update their service collection options
			// NOTE: the dummy image is used to prevent cors problems and get the browser to send the original assignedService cookie
			let u, serviceCollectionImage
			const updateTS = new Date().getTime()
			for(u in response.assignedServiceUrls){
				serviceCollectionImage = new Image();
				serviceCollectionImage.src = String(response.assignedServiceUrls[u]).replace(/[\/\s]+$/g, '') + '/~/clientInterface/updateServiceCollectionOptions/' + response.serviceCollectionSecret + '/' + Buffer.from(window.location.origin).toString('base64') + '/serviceCollectionImage.jpg?_=' + updateTS;
			}
		}

		if((callback || {}).function){
			if(callback.context) {
				callback.function.apply(callback.context, [response]);
			}
			else {
				callback.function(response);
			}
		}
	}).fail(function(error){
		if((callback || {}).function){
			if(callback.context) {
				callback.function.apply(callback.context, [undefined, error]);
			}
			else {
				callback.function(undefined, response);
			}
		}
	});
};

/**
 * set the language properties according to the given langCode without changing the original given storages' ones.
 *
 * @param storage		object - actual storage
 * @param langCode		string - langCode to change to
 * @returns object - actual storage respecting all references except the storage.langCode and storage.__()
 */
store.getLanguageCopy = (storage, langCode) => {
	storage = storage || {}

	if(langCode && langCode !== storage.langCode){
		return {
			...storage,
			langCode,
			__: translationManager.getTranslator(langCode)
		}
	}

	return storage
}

/**
 * this function will be called each time store.get() will be called.
 * it can modify the storage and returns it afterward.
 *
 * @param   storage     object - storage to sanitize
 * @returns storage
 */
store.sanitizeStorage = function(storage){
	if((!storage.user || !(storage.user instanceof User)) && storage.session && storage.session.user){
		storage.user = new User(storage)
	}

	// set translation defaults (a user may not change his langCode but his used one) e.g., send a german mail or render a german route via an english storage.
	storage.langCode = `${(((storage || {}).session || {}).user || {}).langCode || ''}`
	storage.__ = translationManager.getTranslator(storage.langCode)

	return storage
};

/**
 * this function is used to destroy the local browsers' storage
 */
store.destroy = function(){
	clientStore.remove(appConfig.store.name)
}

module.exports = (globalThis.store = store)